/**
 * Colori della console: contrasto WCAG e una variante leggibile su fondo scuro.
 *
 * PERCHÉ serve: l'accento della società arriva dal tema (`--league-brand-vivid`,
 * o `scorebugTheme.accent`) e può essere un blu notte. Sul tabellone pubblico
 * quel colore è **giusto** — è l'identità della squadra — ma usato come testo su
 * un pannello quasi nero (`#08080f`) sparisce: punteggi e nomi delle tile a 8 px
 * diventano illeggibili proprio a bordo campo.
 *
 * Qui il colore non viene sostituito: viene **schiarito quanto basta** perché il
 * rapporto di contrasto con lo sfondo raggiunga la soglia AA (4,5:1). La tinta
 * resta quella della società, cambia solo la luminosità.
 */

export type Rgb = [number, number, number]

/** `#rgb`/`#rrggbb` → [r, g, b]; null se non è un esadecimale. */
export function parseHexColor(hex: string): Rgb | null {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim())
  if (!match) return null
  const raw = match[1].length === 3 ? match[1].split('').map((c) => c + c).join('') : match[1]
  return [
    parseInt(raw.slice(0, 2), 16),
    parseInt(raw.slice(2, 4), 16),
    parseInt(raw.slice(4, 6), 16),
  ]
}

/** Luminanza relativa WCAG (0 = nero, 1 = bianco). */
export function relativeLuminance(color: string): number | null {
  const rgb = parseHexColor(color)
  if (!rgb) return null
  const [r, g, b] = rgb.map((channel) => {
    const c = channel / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** Rapporto di contrasto WCAG (1…21). `1` quando un colore non è leggibile. */
export function contrastRatio(a: string, b: string): number {
  const first = relativeLuminance(a)
  const second = relativeLuminance(b)
  if (first === null || second === null) return 1
  const light = Math.max(first, second)
  const dark = Math.min(first, second)
  return (light + 0.05) / (dark + 0.05)
}

function mixWithWhite(rgb: Rgb, amount: number): Rgb {
  return rgb.map((channel) => Math.round(channel + (255 - channel) * amount)) as Rgb
}

function mixWithBlack(rgb: Rgb, amount: number): Rgb {
  return rgb.map((channel) => Math.round(channel * (1 - amount))) as Rgb
}

function toHex(rgb: Rgb): string {
  return `#${rgb.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`
}

/**
 * Lo stesso colore, schiarito fino al contrasto voluto con lo sfondo.
 *
 * Se il colore è già leggibile lo restituisce **identico**: un verde acceso non
 * deve cambiare tinta per colpa di una regola pensata per il blu notte.
 */
export function readableOnDark(
  color: string,
  background = '#08080f',
  minRatio = 4.5,
): string {
  const rgb = parseHexColor(color)
  if (!rgb) return color
  if (contrastRatio(color, background) >= minRatio) return color
  // 20 passi del 5%: sufficienti per portare un blu notte alla soglia senza
  // scolorire un accento che era quasi leggibile.
  for (let step = 1; step <= 20; step += 1) {
    const candidate = toHex(mixWithWhite(rgb, step / 20))
    if (contrastRatio(candidate, background) >= minRatio) return candidate
  }
  return '#ffffff'
}

/**
 * Lo stesso colore, **scurito** fino al contrasto voluto con lo sfondo.
 *
 * È il gemello di `readableOnDark` per il tema chiaro del tabellone (elenco
 * `/live`, card su fondo bianco): lì il colore di ripiego è bianco e la guardia
 * che schiarisce non lo tocca, quindi il nome della squadra restava bianco su
 * bianco. Se il colore è già leggibile resta **identico**.
 */
export function readableOnLight(
  color: string,
  background = '#ffffff',
  minRatio = 4.5,
): string {
  const rgb = parseHexColor(color)
  if (!rgb) return color
  if (contrastRatio(color, background) >= minRatio) return color
  for (let step = 1; step <= 20; step += 1) {
    const candidate = toHex(mixWithBlack(rgb, step / 20))
    if (contrastRatio(candidate, background) >= minRatio) return candidate
  }
  return '#162133'
}
